<script setup>
import { ref, computed } from 'vue'
import ChartView from '@/components/charts/ChartView.vue'
import { store } from '@/store/appStore.js'
const terrFootfall = computed(() => {
  const byT={}
  store.data.pharmacies.value.forEach(p=>{byT[p.territory]=byT[p.territory]||{footfall:0,count:0}; byT[p.territory].footfall+=p.monthlyFootfall; byT[p.territory].count++})
  return Object.entries(byT).map(([t,v])=>({territory:t,avgFootfall:Math.round(v.footfall/v.count),count:v.count})).sort((a,b)=>b.avgFootfall-a.avgFootfall)
})
const footfallChart = computed(() => ({ labels:terrFootfall.value.map(t=>t.territory), datasets:[{ label:'Avg Monthly Footfall', data:terrFootfall.value.map(t=>t.avgFootfall), backgroundColor:'#17595B' }] }))
const catDist = computed(() => { const c={A:0,B:0,C:0}; store.data.pharmacies.value.forEach(p=>c[p.category]++); return { labels:['Category A','Category B','Category C'], datasets:[{ data:[c.A,c.B,c.C], backgroundColor:['#0F3D3E','#C99A2E','#8B959B'] }] } })

// Growth Analysis — territory revenue, trailing quarter vs. prior quarter.
// Reuses the exact same trailing-window methodology as Business Potential's
// Growth % (last 3 months vs. the 3 before that), rather than inventing a
// second growth formula that could disagree with the first.
const pharmacyTerritory = computed(() => Object.fromEntries(store.data.pharmacies.value.map(p => [p.customerId, p.territory])))
const MONTHS = computed(() => [...new Set(store.data.orders.value.map(o => o.orderDate.slice(0,7)))].sort())
const last3 = computed(() => MONTHS.value.slice(-3))
const prior3 = computed(() => MONTHS.value.slice(-6, -3))
function sumInMonths(orders, months) {
  const set = new Set(months)
  return Math.round(orders.filter(o => set.has(o.orderDate.slice(0,7))).reduce((s,o) => s + o.orderValue, 0))
}
const territoryGrowth = computed(() => {
  const byTerr = {}
  store.data.orders.value.forEach(o => {
    const t = pharmacyTerritory.value[o.customerId] || 'Unknown'
    byTerr[t] = byTerr[t] || []
    byTerr[t].push(o)
  })
  return Object.entries(byTerr).map(([territory, orders]) => {
    const current = sumInMonths(orders, last3.value)
    const prior = sumInMonths(orders, prior3.value)
    const rawGrowthPct = prior > 0 ? Math.round((current - prior) / prior * 100) : (current > 0 ? null : 0)
    // Display cap, same treatment as the POB Dashboard leaderboard: a small
    // territory with a low prior-quarter base can swing 300%+ on the raw
    // math (real, but reads as broken data). Capped display, raw value kept
    // for sorting so genuine high-growth territories still rank correctly.
    const growthPct = rawGrowthPct === null ? null : Math.min(rawGrowthPct, 150)
    const capped = rawGrowthPct !== null && rawGrowthPct > 150
    return { territory, current, prior, growthPct, rawGrowthPct, capped }
  }).sort((a,b) => (b.rawGrowthPct ?? -999) - (a.rawGrowthPct ?? -999))
})
const growthChart = computed(() => ({
  labels: territoryGrowth.value.map(t => t.territory),
  datasets: [{ label: 'Growth % (QoQ, capped at 150%)', data: territoryGrowth.value.map(t => t.growthPct ?? 0), backgroundColor: territoryGrowth.value.map(t => (t.growthPct ?? 0) >= 0 ? '#3A9B5C' : '#C1392B') }]
}))

// Product Adoption Trends — real campaign adoptionRate + territoryPerformance,
// not fabricated. territoryPerformance is a 0-100 performance score per
// territory per campaign, used here as the adoption proxy across geography.
const campaigns = computed(() => store.data.campaigns.value)
const adoptionChart = computed(() => ({
  labels: campaigns.value.map(c => c.productName),
  datasets: [{ label: 'Adoption Rate %', data: campaigns.value.map(c => c.adoptionRate), backgroundColor: '#5B4E9E' }]
}))
const selectedCampaignIdx = ref(0)
const selectedCampaign = computed(() => campaigns.value[selectedCampaignIdx.value])
const campaignTerritoryChart = computed(() => selectedCampaign.value ? {
  labels: selectedCampaign.value.territoryPerformance.map(t => t.territory),
  datasets: [{ label: 'Performance Score', data: selectedCampaign.value.territoryPerformance.map(t => t.performanceScore), backgroundColor: '#C99A2E' }]
} : { labels: [], datasets: [] })

// Market Opportunity Assessment — aggregates the exact opportunityGapValue
// formula Business Potential already computes (category ceiling x potential
// score, minus actual purchase value), summed by territory. Reused rather
// than a second opportunity metric that could disagree with Module 8's.
const CEIL = { A:5000, B:3000, C:1500 }
const marketOpportunity = computed(() => {
  const byPh = {}
  store.data.orders.value.forEach(o => { byPh[o.customerId] = (byPh[o.customerId]||0) + o.orderValue })
  const byTerr = {}
  store.data.pharmacies.value.forEach(p => {
    const potentialValue = (CEIL[p.category]||2000) * (p.potentialScore/100)
    const actual = byPh[p.customerId] || 0
    const gap = Math.max(0, potentialValue - actual)
    byTerr[p.territory] = byTerr[p.territory] || { territory: p.territory, gap: 0, pharmacyCount: 0 }
    byTerr[p.territory].gap += gap
    byTerr[p.territory].pharmacyCount += 1
  })
  return Object.values(byTerr).map(t => ({ ...t, gap: Math.round(t.gap) })).sort((a,b) => b.gap - a.gap)
})

const notes = [
  { title:'Generic manufacturers', text:'Regional generic manufacturers compete primarily on price in antibiotics and pain-management categories.' },
  { title:'Cardiac & diabetes', text:'Branded competitors maintain stronger share in cardiac and diabetes divisions, where prescriber loyalty is higher.' },
  { title:'Rural distribution', text:'Coverage gaps in rural Matabeleland South and Masvingo represent open opportunity relative to competitor footprint.' }
]
</script>
<template>
  <div>
    <div class="card module-header"><h2>Territory insights across {{ store.data.pharmacies.value.length }} pharmacies</h2></div>
    <div class="split-main-side" style="margin-bottom:16px;">
      <div class="card" style="padding:18px 20px;"><div class="eyebrow" style="margin-bottom:8px;">Average Monthly Footfall by Territory</div><ChartView type="bar" :labels="footfallChart.labels" :datasets="footfallChart.datasets" :height="260" key="footfall-bar" /></div>
      <div class="card" style="padding:18px 20px;"><div class="eyebrow" style="margin-bottom:8px;">Pharmacy Category Distribution</div><ChartView type="doughnut" :labels="catDist.labels" :datasets="catDist.datasets" :height="260" key="category-donut" /></div>
    </div>

    <div class="card" style="padding:18px 20px;margin-bottom:16px;">
      <div class="eyebrow" style="margin-bottom:8px;">Growth Analysis — Territory Revenue, Trailing Quarter vs. Prior Quarter</div>
      <ChartView type="bar" :labels="growthChart.labels" :datasets="growthChart.datasets" :height="240" key="growth-bar" />
      <div class="table-wrap" style="margin-top:14px;">
        <table class="data-table">
          <thead><tr><th>Territory</th><th>This Quarter (USD)</th><th>Prior Quarter (USD)</th><th>Growth %</th></tr></thead>
          <tbody>
            <tr v-for="t in territoryGrowth" :key="t.territory">
              <td class="cell-strong">{{ t.territory }}</td><td class="cell-muted">${{ t.current.toLocaleString() }}</td><td class="cell-muted">${{ t.prior.toLocaleString() }}</td>
              <td :style="t.growthPct === null ? '' : (t.growthPct >= 0 ? 'color:var(--status-green);font-weight:700;' : 'color:var(--status-red);font-weight:700;')">{{ t.growthPct === null ? 'New' : (t.capped ? '150%+' : (t.growthPct >= 0 ? '+' : '') + t.growthPct + '%') }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <div class="card" style="padding:18px 20px;margin-bottom:16px;">
      <div class="eyebrow" style="margin-bottom:8px;">Product Adoption Trends</div>
      <ChartView type="bar" :labels="adoptionChart.labels" :datasets="adoptionChart.datasets" :height="220" key="adoption-bar" />
      <div class="toolbar" style="border-bottom:none;padding:14px 0 8px;">
        <select class="filter-select" v-model.number="selectedCampaignIdx">
          <option v-for="(c,i) in campaigns" :key="c.campaignName" :value="i">{{ c.productName }} — {{ c.campaignName }}</option>
        </select>
        <span class="result-count">Territory-wise performance for the selected campaign</span>
      </div>
      <ChartView type="bar" :labels="campaignTerritoryChart.labels" :datasets="campaignTerritoryChart.datasets" :height="220" :key="'camp-terr-'+selectedCampaignIdx" />
    </div>

    <div class="card" style="padding:18px 20px;margin-bottom:16px;">
      <div class="eyebrow" style="margin-bottom:8px;">Market Opportunity Assessment</div>
      <p class="muted" style="font-size:11.5px;margin:0 0 12px;">Untapped potential by territory — the same Opportunity Gap figure Business Potential computes per pharmacy, summed by territory.</p>
      <div class="table-wrap">
        <table class="data-table">
          <thead><tr><th>Territory</th><th>Pharmacies</th><th>Untapped Opportunity (USD)</th></tr></thead>
          <tbody>
            <tr v-for="t in marketOpportunity" :key="t.territory">
              <td class="cell-strong">{{ t.territory }}</td><td class="cell-muted">{{ t.pharmacyCount }}</td>
              <td :style="t.gap>0?'color:var(--color-accent);font-weight:600;':'color:var(--status-green);'">{{ t.gap>0?'$'+t.gap.toLocaleString():'Fully realized' }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <div class="card" style="padding:18px 20px;">
      <div class="eyebrow" style="margin-bottom:12px;">Competitor Insights <span class="gap-tag" style="margin-left:6px;">Illustrative, generic — no named competitors</span></div>
      <div style="display:grid;grid-template-columns:repeat(auto-fit,minmax(220px,1fr));gap:12px;">
        <div v-for="n in notes" :key="n.title" class="card" style="padding:14px 16px;box-shadow:none;"><strong style="display:block;margin-bottom:4px;font-size:13px;">{{ n.title }}</strong><span class="muted" style="font-size:12.5px;">{{ n.text }}</span></div>
      </div>
    </div>
  </div>
</template>
